import React, { useState, useEffect, useContext } from "react";
import { useHistory, Link } from "react-router-dom";
import queryString from "query-string";

import { Grid, IconButton, Divider, Button, Fab } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CustomerDocumentIcon from "@material-ui/icons/FolderShared";
import AddIcon from "@material-ui/icons/Add";

import { listCustomerDocuments } from "../api/DocumentAPI";
import { AppContext } from "../context/AppContext";
import { Document, Documents, DocumentsQueryParams } from "../model/Document";

import translate from "../i18n/Translator";
import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import ValidatedInput from "../components/ValidatedInput";
import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import DocumentMenu from "./DocumentMenu";

export default function DocumentsList() {
    const context = useContext(AppContext);
    const history = useHistory();
    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = (): DocumentsQueryParams => {
        return {
            "search": typeof qs["search"] === "string" ? qs["search"] as string : ""
        } as DocumentsQueryParams;
    };

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Documents>();
    const [params, setParams] = useState<DocumentsQueryParams>(paramsFromQueryString);
    const [workingParams, setWorkingParams] = useState<DocumentsQueryParams>(paramsFromQueryString);

    const [customerdocument, setCustomerDocument] = useState<Document>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    useEffect(() => {
        let offset = getOffset(page, pageSize);
        listCustomerDocuments(context.session!.tenant!.id, pageSize, offset, params).then((response) => {
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    }, [context.session, page, pageSize, params]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        qs["page"] = "1";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);

        setParams(workingParams);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedOptions = (customerdocument: Document) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCustomerDocument(customerdocument);
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setCustomerDocument(undefined);
    };

    return (
        <Pagination title={translate("documents.title")} icon={<CustomerDocumentIcon />}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize} action={context.isGranted("DocumentsCreate") ?
                <Link to="/documents/new">
                    <Fab color="primary" size="small" title={translate("buttons.add") as string}>
                        <AddIcon />
                    </Fab>
                </Link>
                : undefined}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} sm>
                        <ValidatedInput type="text" id="search" name="search" label={translate("documents.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("documents.empty") as string}
                columns={[
                    {
                        title: translate("documents.name") as string,
                        converter: (customerdocument) => (
                            <Ellipsis text={customerdocument.name} lenght={100} uppercased={false} />
                        ),
                        xs: true
                    },
                    {
                        title: translate("documents.description") as string,
                        converter: (customerdocument) => (
                            <Ellipsis text={customerdocument.description || ""} lenght={100} uppercased={false} />
                        ),
                        xs: false,
                        sm: 6,
                        lg: 7,
                        xl: 7
                    },
                    {
                        title: (
                            <IconButton size="small" style={{ "visibility": "hidden" }} disabled>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        converter: (customerdocument) => (
                            <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(customerdocument)}>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        justify: "flex-end",
                        xs: "auto"
                    }
                ]} />
            { customerdocument && anchorEl &&
                <DocumentMenu customerdocument={customerdocument} anchor={anchorEl} onClose={onCloseOption} />
            }
        </Pagination>
    );
}